import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { doc, getDoc, onSnapshot, setDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { playSkitter } from '../utils/soundManager';

// ── mood pools ────────────────────────────────────────────────────────────────

const CURSE_MOODS = [
  "Send it.",
  "Let it fly.",
  "No hesitation.",
  "Full send.",
  "Don't hold back.",
  "Make it count.",
  "The moment is now.",
  "Go off.",
  "You know you want to.",
  "Commit to the bit.",
];

const BLESS_MOODS = [
  "Shine on.",
  "Be the light.",
  "Spread it.",
  "Radiate goodness.",
  "You're one of the good ones.",
  "Good energy only.",
  "Go ahead.",
  "The world needs this.",
  "Pass it on.",
  "Make it count.",
];

// ── helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10); // "2026-05-19"

const getISOWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    d.getFullYear() +
    '-W' +
    String(
      1 +
        Math.round(
          ((d.getTime() - week1.getTime()) / 86400000 -
            3 +
            ((week1.getDay() + 6) % 7)) /
            7
        )
    ).padStart(2, '0')
  );
};

const periodDocId = period => {
  const t = today();
  if (period === 'today') return `day-${t}`;
  if (period === 'week') return `week-${getISOWeek()}`;
  if (period === 'month') return `month-${t.slice(0, 7)}`;
  return null; // alltime uses meta/curseBless
};

const msToMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight - now;
};

const fmtCountdown = ms => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const getFunnyLine = (curses, blessings) => {
  const total = curses + blessings;
  if (total === 0) return "The vibes are quiet. Make your mark. ✨";
  const pct = Math.round((curses / total) * 100);
  if (pct === 100) return "All curses, no blessings. Bold crowd. ☠️";
  if (pct >= 80) return "Heavy on the curses. The balance shall be restored. ⚖️";
  if (pct >= 60) return "Slightly cursed, undeniably iconic. 📝";
  if (pct === 50) return "Perfectly balanced. As all things should be. ☯️";
  if (pct <= 20) return "Overwhelmingly blessed. The people have spoken. 👑";
  if (pct <= 40) return "More blessings than curses. A good sign. ✨";
  return "The vibes are mixed. The dashboard is watching. 👁️";
};

const PERIOD_LABELS = {
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
  alltime: 'All Time',
};

const PERIOD_FLAVOR = {
  today: (c, b) => `Today: ${c} curse${c !== 1 ? 's' : ''}, ${b} blessing${b !== 1 ? 's' : ''}. The day is still young.`,
  week: (c, b) => `This week: ${c} curses, ${b} blessings. The vibes are being tracked.`,
  month: (c, b) => `This month: ${b + c} total votes. ${b} blessings holding strong.`,
  alltime: (c, b) => `All time: ${b + c} votes cast. ${b} blessings, ${c} curses. The record stands.`,
};

// ── component ─────────────────────────────────────────────────────────────────

const CurseBless = () => {
  const [activePeriod, setActivePeriod] = useState('today');
  const [periodCounts, setPeriodCounts] = useState({ curses: 0, blessings: 0 });
  const [allTime, setAllTime] = useState({ curses: 0, blessings: 0 });
  const [chartData, setChartData] = useState([]);
  const [hasVotedToday, setHasVotedToday] = useState(
    localStorage.getItem('curseBlessDate') === today()
  );
  const [countdown, setCountdown] = useState('');
  const [justVoted, setJustVoted] = useState(null);
  const [curseMood, setCurseMood] = useState(() => CURSE_MOODS[Math.floor(Math.random() * CURSE_MOODS.length)]);
  const [blessMood, setBlessMood] = useState(() => BLESS_MOODS[Math.floor(Math.random() * BLESS_MOODS.length)]);
  const unsubRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setCurseMood(prev => {
        const pool = CURSE_MOODS.filter(m => m !== prev);
        return pool[Math.floor(Math.random() * pool.length)];
      });
      setBlessMood(prev => {
        const pool = BLESS_MOODS.filter(m => m !== prev);
        return pool[Math.floor(Math.random() * pool.length)];
      });
    }, 3500);
    return () => clearInterval(id);
  }, []);

  // Live all-time listener
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'meta', 'curseBless'), snap => {
      setAllTime({ curses: snap.data()?.curses ?? 0, blessings: snap.data()?.blessings ?? 0 });
    });
    return unsub;
  }, []);

  // Switch period listener
  useEffect(() => {
    if (unsubRef.current) unsubRef.current();
    if (activePeriod === 'alltime') {
      setPeriodCounts(allTime);
      return;
    }
    const ref = doc(db, 'curseBlessHistory', periodDocId(activePeriod));
    unsubRef.current = onSnapshot(ref, snap => {
      setPeriodCounts({ curses: snap.data()?.curses ?? 0, blessings: snap.data()?.blessings ?? 0 });
    });
    return () => { if (unsubRef.current) unsubRef.current(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeriod]);

  // Sync alltime → period counts when on alltime tab
  useEffect(() => {
    if (activePeriod === 'alltime') setPeriodCounts(allTime);
  }, [allTime, activePeriod]);

  // Fetch 7-day chart once on mount
  useEffect(() => {
    const ids = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
      return { id: `day-${y}-${mo}-${da}`, label: day };
    });

    Promise.all(ids.map(({ id }) => getDoc(doc(db, 'curseBlessHistory', id))))
      .then(snaps => {
        setChartData(
          ids.map(({ label }, i) => ({
            label,
            curses: snaps[i]?.data()?.curses ?? 0,
            blessings: snaps[i]?.data()?.blessings ?? 0,
          }))
        );
      })
      .catch(() => {});
  }, []);

  // Midnight countdown
  useEffect(() => {
    if (!hasVotedToday) return;
    const tick = () => setCountdown(fmtCountdown(msToMidnight()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [hasVotedToday]);

  const vote = async choice => {
    if (hasVotedToday) return;
    const t = today();
    const refs = [
      doc(db, 'curseBlessHistory', `day-${t}`),
      doc(db, 'curseBlessHistory', `week-${getISOWeek()}`),
      doc(db, 'curseBlessHistory', `month-${t.slice(0, 7)}`),
      doc(db, 'curseBlessHistory', `year-${t.slice(0, 4)}`),
      doc(db, 'meta', 'curseBless'),
    ];
    await Promise.all(refs.map(r => setDoc(r, { [choice]: increment(1) }, { merge: true })));
    localStorage.setItem('curseBlessDate', t);
    setHasVotedToday(true);
    setJustVoted(choice);
    playSkitter();
    setTimeout(() => setJustVoted(null), 1200);
  };

  const { curses, blessings } = periodCounts;
  const total = curses + blessings;
  const cursePercent = total > 0 ? Math.round((curses / total) * 100) : 50;
  const chartMax = Math.max(...chartData.map(d => d.curses + d.blessings), 1);

  return (
    <Wrapper>
      <Header>
        <Title>☠️ Abel's Fate Dashboard ☠️</Title>
        <Subtitle>Real-time vibes analytics — curse or bless, the data never lies</Subtitle>
      </Header>

      {/* Vote buttons */}
      {!hasVotedToday ? (
        <Buttons>
          <CurseBtn onClick={() => vote('curses')} $pulse={justVoted === 'curses'}>
            ☠️ CURSE HIM
            <BtnSub key={curseMood}>{curseMood}</BtnSub>
          </CurseBtn>
          <BlessBtn onClick={() => vote('blessings')} $pulse={justVoted === 'blessings'}>
            ✨ BLESS HIM
            <BtnSub key={blessMood}>{blessMood}</BtnSub>
          </BlessBtn>
        </Buttons>
      ) : (
        <VotedMsg>
          <VotedIcon>{justVoted === 'curses' || localStorage.getItem('curseBlessVote') === 'curses' ? '☠️' : '✨'}</VotedIcon>
          Vote logged. The universe has been notified.
          <Countdown>Resets in {countdown}</Countdown>
          <VotedSub>Check back tomorrow to cast your vote again.</VotedSub>
        </VotedMsg>
      )}

      {/* Bar */}
      <SplitBar>
        <CurseFill style={{ width: `${cursePercent}%` }} />
        <BlessFill style={{ width: `${100 - cursePercent}%` }} />
      </SplitBar>
      <SplitLabels>
        <span>☠️ {cursePercent}%</span>
        <FunnyLine>{getFunnyLine(allTime.curses, allTime.blessings)}</FunnyLine>
        <span>{100 - cursePercent}% ✨</span>
      </SplitLabels>

      {/* Period tabs */}
      <Tabs>
        {Object.entries(PERIOD_LABELS).map(([key, label]) => (
          <Tab key={key} $active={activePeriod === key} onClick={() => setActivePeriod(key)}>
            {label}
          </Tab>
        ))}
      </Tabs>

      <PeriodStats>
        <StatRow>
          <Stat color="#cc4444">
            <StatNum>{curses.toLocaleString()}</StatNum>
            <StatLabel>☠️ Curses</StatLabel>
          </Stat>
          <StatDivider />
          <Stat color="#9400d3">
            <StatNum>{blessings.toLocaleString()}</StatNum>
            <StatLabel>✨ Blessings</StatLabel>
          </Stat>
          <StatDivider />
          <Stat color="rgba(255,255,255,0.5)">
            <StatNum>{(curses + blessings).toLocaleString()}</StatNum>
            <StatLabel>Total Judgements</StatLabel>
          </Stat>
        </StatRow>
        <FlavorText>{PERIOD_FLAVOR[activePeriod]?.(curses, blessings)}</FlavorText>
      </PeriodStats>

      {/* 7-day chart */}
      {chartData.length > 0 && (
        <ChartSection>
          <ChartTitle>Last 7 Days — Vibes Tracker™</ChartTitle>
          <Chart>
            {chartData.map((day, i) => {
              const dayTotal = day.curses + day.blessings;
              const cursePct = dayTotal > 0 ? (day.curses / dayTotal) * 100 : 50;
              const barHeight = Math.max(4, Math.round((dayTotal / chartMax) * 80));
              return (
                <ChartBar key={i}>
                  <BarArea>
                    {dayTotal > 0 && <BarCount>{dayTotal}</BarCount>}
                    <BarStack style={{ height: barHeight }}>
                      <BarCurse style={{ height: `${cursePct}%` }} />
                      <BarBless style={{ height: `${100 - cursePct}%` }} />
                    </BarStack>
                  </BarArea>
                  <BarLabel>{day.label}</BarLabel>
                </ChartBar>
              );
            })}
          </Chart>
          <ChartLegend>
            <LegendItem color="#cc4444">☠️ Curses</LegendItem>
            <LegendItem color="#7b00d4">✨ Blessings</LegendItem>
          </ChartLegend>
        </ChartSection>
      )}
    </Wrapper>
  );
};

export default CurseBless;

// ── styles ────────────────────────────────────────────────────────────────────

const popIn = keyframes`
  0%,100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const glow = keyframes`
  0%, 100% { text-shadow: 0 0 12px rgba(148, 0, 211, 0.6); }
  50% { text-shadow: 0 0 30px rgba(148, 0, 211, 1), 0 0 60px rgba(148, 0, 211, 0.35); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const moodFade = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 0.6; transform: translateY(0); }
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 660px;
  margin: 40px auto;
  padding: 32px 24px;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(76, 0, 101, 0.55);
  border-radius: 18px;
  font-family: 'Creepster', Arial, sans-serif;
  animation: ${slideUp} 0.55s ease both;
  @media (max-width: 430px) { padding: 20px 12px; }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.9rem;
  margin: 0 0 6px;
  animation: ${glow} 3s ease infinite;
  @media (max-width: 430px) { font-size: 1.4rem; }
`;

const Subtitle = styled.p`
  color: rgba(255,255,255,0.45);
  font-size: 0.85rem;
  margin: 0;
`;

const Buttons = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 22px;
  @media (max-width: 430px) { gap: 8px; }
`;

const VoteBtn = styled.button`
  flex: 1;
  padding: 16px 10px 12px;
  border-radius: 12px;
  border: 3px solid;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.35rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  transition: transform 0.15s, box-shadow 0.15s;
  animation: ${({ $pulse }) => ($pulse ? popIn : 'none')} 0.4s ease;
  &:active { transform: scale(0.95); }
  @media (max-width: 430px) { font-size: 1.05rem; padding: 12px 6px; }
`;

const CurseBtn = styled(VoteBtn)`
  background: rgba(80, 0, 0, 0.65);
  border-color: #880000;
  color: #ff7777;
  &:hover { box-shadow: 0 0 18px rgba(200,0,0,0.45); transform: scale(1.03); }
`;

const BlessBtn = styled(VoteBtn)`
  background: rgba(40, 0, 60, 0.65);
  border-color: #660099;
  color: #cc44ff;
  &:hover { box-shadow: 0 0 18px rgba(148,0,211,0.45); transform: scale(1.03); }
`;

const BtnSub = styled.span`
  font-size: 0.75rem;
  animation: ${moodFade} 0.35s ease both;
`;

const VotedMsg = styled.div`
  text-align: center;
  padding: 16px;
  background: rgba(76, 0, 101, 0.2);
  border: 1px solid rgba(148, 0, 211, 0.3);
  border-radius: 10px;
  margin-bottom: 22px;
  color: rgba(255,255,255,0.8);
  font-size: 1rem;
`;

const VotedIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 6px;
`;

const Countdown = styled.div`
  font-size: 1.6rem;
  color: #cc44ff;
  letter-spacing: 3px;
  margin: 8px 0 4px;
`;

const VotedSub = styled.p`
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
  margin: 4px 0 0;
`;

const SplitBar = styled.div`
  display: flex;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: rgba(255,255,255,0.08);
  margin-bottom: 8px;
`;

const CurseFill = styled.div`
  background: linear-gradient(90deg, #6b0000, #cc0000);
  transition: width 0.7s ease;
`;

const BlessFill = styled.div`
  background: linear-gradient(90deg, #5500aa, #9400d3);
  transition: width 0.7s ease;
`;

const SplitLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.55);
  margin-bottom: 20px;
  gap: 8px;
`;

const FunnyLine = styled.span`
  flex: 1;
  text-align: center;
  color: rgba(255,255,255,0.4);
  font-size: 0.78rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  flex: 1;
  min-width: 70px;
  padding: 8px 4px;
  border-radius: 8px;
  border: 1.5px solid ${({ $active }) => ($active ? 'rgba(148,0,211,0.8)' : 'rgba(255,255,255,0.1)')};
  background: ${({ $active }) => ($active ? 'rgba(76,0,101,0.55)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : 'rgba(255,255,255,0.45)')};
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: rgba(148,0,211,0.5); color: white; }
`;

const PeriodStats = styled.div`
  margin-bottom: 24px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  padding: 16px 10px;
  margin-bottom: 10px;
`;

const Stat = styled.div`
  text-align: center;
  flex: 1;
`;

const StatNum = styled.div`
  font-size: 2rem;
  color: white;
  line-height: 1;
  @media (max-width: 430px) { font-size: 1.4rem; }
`;

const StatLabel = styled.div`
  font-size: 0.78rem;
  color: rgba(255,255,255,0.4);
  margin-top: 4px;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 36px;
  background: rgba(255,255,255,0.1);
`;

const FlavorText = styled.p`
  text-align: center;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.4);
  margin: 0;
  font-style: italic;
`;

const ChartSection = styled.div`
  margin-top: 8px;
`;

const ChartTitle = styled.p`
  font-size: 0.85rem;
  color: rgba(255,255,255,0.4);
  text-align: center;
  margin: 0 0 12px;
  letter-spacing: 1px;
`;

const Chart = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-around;
  height: 110px;
  gap: 4px;
  padding: 0 4px;
  margin-bottom: 8px;
`;

const ChartBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BarArea = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

const BarStack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 4px 4px 0 0;
  overflow: hidden;
  min-height: 4px;
`;

const BarCurse = styled.div`
  background: #991111;
  transition: height 0.5s ease;
`;

const BarBless = styled.div`
  background: #5500aa;
  transition: height 0.5s ease;
`;

const BarLabel = styled.span`
  font-size: 0.7rem;
  color: rgba(255,255,255,0.4);
  height: 18px;
  line-height: 18px;
  margin-top: 4px;
  flex-shrink: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
`;

const BarCount = styled.span`
  font-size: 0.65rem;
  color: rgba(255,255,255,0.4);
  margin-bottom: 2px;
  flex-shrink: 0;
`;

const ChartLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 6px;
`;

const LegendItem = styled.span`
  font-size: 0.75rem;
  color: ${({ color }) => color};
`;
